import React from "react";
import {
  Activity,
  ArrowUpRight,
  BadgeCheck,
  Boxes,
  Clock3,
  Gem,
  Sparkles,
  Target,
  Wallet,
} from "lucide-react";
import { motion } from "framer-motion";
import PageHero from "../components/common/PageHero";
import StatGrid from "../components/common/StatGrid";
import DataTableCard from "../components/common/DataTableCard";
import KanbanMini from "../components/common/KanbanMini";

const pulseCards = [
  { title: "Lot throughput", value: "84%", meta: "Above target today" },
  { title: "MM queue", value: "11 lots", meta: "Needs balancing" },
  { title: "Approval risk", value: "2 agents", meta: "Watch closely" },
  { title: "High margin mix", value: "63%", meta: "Premium range active" },
];

export default function Dashboard() {
  return (
    <div className="erp-page-grid">
      <PageHero
        badge="Executive dashboard"
        title="Clean command center for your entire operation."
        subtitle="A simplified software-style control room with faster navigation, clearer numbers, lighter colors and more confident actions."
        actions={
          <>
            <motion.button
              type="button"
              className="erp-btn erp-btn-ghost"
              whileTap={{ scale: 0.97 }}
            >
              <Clock3 size={15} /> Shift summary
            </motion.button>
            <motion.button
              type="button"
              className="erp-btn erp-btn-primary"
              whileTap={{ scale: 0.97 }}
            >
              <Sparkles size={15} /> Open daily view
            </motion.button>
          </>
        }
      />

      <StatGrid
        items={[
          {
            title: "Today production",
            value: "₹18.4L",
            meta: "49 active lots across the floor",
            trend: "+8.2%",
            tone: "success",
            icon: Gem,
          },
          {
            title: "On-time completion",
            value: "92%",
            meta: "Better than last week",
            trend: "+4.1%",
            tone: "success",
            icon: Target,
          },
          {
            title: "Approval exposure",
            value: "₹6.8L",
            meta: "8 open settlements pending",
            trend: "Watch",
            tone: "warning",
            icon: Wallet,
          },
          {
            title: "Inventory health",
            value: "Strong",
            meta: "No critical stock-out today",
            trend: "Stable",
            tone: "neutral",
            icon: Boxes,
          },
        ]}
      />

      <div className="erp-grid erp-grid-2">
        <motion.section
          className="erp-card"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28 }}
        >
          <div className="erp-card-head">
            <div>
              <h3>Operations pulse</h3>
              <p>Focused highlights for dispatch, queue pressure and lot velocity.</p>
            </div>
            <span className="erp-pill success">Live</span>
          </div>

          <KanbanMini items={pulseCards} />

          <div className="erp-divider" />

          <div className="erp-list">
            {[
              ["MM backlog easing", "4 lots reassigned to keep the line balanced."],
              ["Premium mix rising", "Oval and certified designs are driving better margins."],
              ["Approval aging low", "Most outstanding dispatches are under 7 days."],
            ].map(([title, description]) => (
              <motion.div
                key={title}
                className="erp-list-row"
                whileHover={{ x: 2 }}
                whileTap={{ scale: 0.99 }}
              >
                <div className="erp-list-icon">
                  <ArrowUpRight size={15} />
                </div>
                <div>
                  <strong>{title}</strong>
                  <p>{description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section
          className="erp-card"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.32 }}
        >
          <div className="erp-card-head">
            <div>
              <h3>Daily priorities</h3>
              <p>The few things that deserve immediate attention.</p>
            </div>
            <Activity size={18} />
          </div>

          <div className="erp-progress-stack">
            {[
              ["Dispatch readiness", 76, "Invoices and packing sync is almost done."],
              ["Lot trace closure", 58, "Older split lots need final linking."],
              ["Worker utilization", 87, "Strong capacity coverage in polishing."],
            ].map(([label, value, text]) => (
              <div key={label} className="erp-progress-card">
                <div className="erp-progress-top">
                  <strong>{label}</strong>
                  <span>{value}%</span>
                </div>
                <div className="erp-progress-line">
                  <span style={{ width: `${value}%` }} />
                </div>
                <p>{text}</p>
              </div>
            ))}
          </div>

          <div className="erp-callout soft">
            <BadgeCheck size={16} />
            <div>
              <strong>System note</strong>
              <span>All sections are now grouped into a lighter, product-style workspace.</span>
            </div>
          </div>
        </motion.section>
      </div>

      <div className="erp-grid erp-grid-3">
        {[
          ["Revenue mix", "High-grade polished pieces are contributing the best margin spread today."],
          ["Bottleneck tracker", "MM is still the slowest stage, but queue burn is improving every hour."],
          ["Floor confidence", "Return rate and process loss are both sitting in the safe band."],
        ].map(([title, description]) => (
          <motion.section
            key={title}
            className="erp-card"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            whileHover={{ y: -4 }}
            whileTap={{ scale: 0.99 }}
          >
            <div className="erp-card-head">
              <div>
                <h3>{title}</h3>
                <p>{description}</p>
              </div>
            </div>
          </motion.section>
        ))}
      </div>

      <DataTableCard
        title="Live lots"
        subtitle="Active lots, stage status, ownership and commercial priority in a single simple table."
        columns={[
          { key: "lot", label: "Lot" },
          { key: "product", label: "Product" },
          { key: "stage", label: "Stage" },
          { key: "owner", label: "Owner" },
          { key: "value", label: "Value" },
          { key: "status", label: "Status" },
        ]}
        rows={[
          {
            lot: "LOT-2401",
            product: "Ruby Oval • Premium",
            stage: "MM",
            owner: "Ramesh",
            value: "₹3.2L",
            status: <span className="erp-pill warning">Needs push</span>,
          },
          {
            lot: "LOT-2405",
            product: "Emerald Pair • Certified",
            stage: "Final Assorting",
            owner: "Mohan",
            value: "₹5.8L",
            status: <span className="erp-pill success">Ready</span>,
          },
          {
            lot: "LOT-2412",
            product: "Amethyst Line • Standard",
            stage: "Ghat",
            owner: "Pooja",
            value: "₹1.6L",
            status: <span className="erp-pill neutral">Steady</span>,
          },
        ]}
      />
    </div>
  );
}
